/**
 * Trial Ended Modal (exige ação)
 *
 * Exibido quando o backend migra o usuário do trial expirado para o plano
 * gratuito (flag persistida `trial_just_ended`). O modal EXIGE uma decisão do
 * usuário — não pode ser dispensado por X, tecla ESC ou clique fora. Ele só é
 * fechado (e a flag só é reconhecida via `ackTrialEnded`) quando o usuário
 * escolhe uma das duas ações: ir para a tela de planos ou continuar no plano
 * gratuito. Enquanto nenhuma ação for tomada, a flag permanece pendente no
 * backend e o modal reaparece no próximo acesso.
 */
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";

interface TrialEndedModalProps {
  open: boolean;
  onClose: () => void;
}

export function TrialEndedModal({ open, onClose }: Readonly<TrialEndedModalProps>) {
  const navigate = useNavigate();

  return (
    <Dialog open={open}>
      {/* Modal não-dismissível: `[&>button]:hidden` esconde o X do DialogContent
          e os handlers abaixo impedem fechar por ESC/clique-fora. O modal só
          fecha via um dos dois botões de ação. */}
      <DialogContent
        className="sm:max-w-lg text-center [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <Sparkles className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <DialogTitle className="text-2xl font-bold">Não perca a direção do seu dinheiro</DialogTitle>
          <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
            Seu período de teste gratuito foi finalizado.
          </p>
          <DialogDescription className="text-base">
            Você foi direcionado para a versão gratuita do Librattum. Para continuar olhando para
            frente com clareza, antecipar despesas futuras e evitar surpresas, ative seu plano completo.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-3">
          <Button
            className="w-full py-6 text-base font-semibold"
            onClick={() => {
              onClose();
              navigate("/account?tab=plan");
            }}
          >
            Garantir inteligência financeira
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <Button variant="outline" className="w-full py-5" onClick={onClose}>
            Continuar no plano gratuito
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TrialEndedModal;
